/**
 * Read-only просмотр сохранённого проекта (E10).
 * Показывает шаги в человекочитаемом виде: отрасль, модели, параметры, результаты.
 */

import { useEffect, useState } from "react";
import type {
  CalcPreviewResult,
  ClientAccessProject,
  ProjectStep,
  PublicModelCard,
  PublicModelForm,
} from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";

type ClientProjectViewProps = {
  api: PublicCatalogApi;
  project: ClientAccessProject;
  onRestore: (project: ClientAccessProject) => void;
  onClose: () => void;
};

const STEP_ORDER = ["industry", "models", "status", "params", "result"] as const;

const STEP_TITLES: Record<(typeof STEP_ORDER)[number], string> = {
  industry: "Отрасль",
  models: "Выбранные модели",
  status: "Статус диалога",
  params: "Параметры",
  result: "Результаты",
};

const STATUS_LABELS: Record<string, string> = {
  beginner: "Начинающий",
  peer: "Равный диалог",
  expert: "Эксперт",
};

type CatalogLabels = {
  industryName: string | null;
  modelsById: Map<string, PublicModelCard>;
  formByModelId: Map<string, PublicModelForm>;
};

export function ClientProjectView({ api, project, onRestore, onClose }: ClientProjectViewProps) {
  const [labels, setLabels] = useState<CatalogLabels>({
    industryName: null,
    modelsById: new Map(),
    formByModelId: new Map(),
  });

  const industryId = findIndustryId(project.steps);
  const modelIds = collectModelIds(project.steps);
  const modelIdsKey = modelIds.join("|");

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const [industries, models] = await Promise.all([
        api.listIndustries().catch(() => []),
        api.listModels(industryId).catch(() => []),
      ]);

      const forms = await Promise.all(
        modelIdsKey
          .split("|")
          .filter(Boolean)
          .map(async (modelId) => {
            try {
              return [modelId, await api.getModelForm(modelId)] as const;
            } catch {
              return null;
            }
          }),
      );

      if (cancelled) {
        return;
      }

      setLabels({
        industryName: industries.find((row) => row.id === industryId)?.name ?? null,
        modelsById: new Map(models.map((row) => [row.id, row])),
        formByModelId: new Map(forms.filter((row): row is [string, PublicModelForm] => row !== null)),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [api, industryId, modelIdsKey]);

  const ordered = STEP_ORDER.flatMap((stepType) =>
    project.steps.filter((step) => step.stepType === stepType),
  );

  return (
    <section className="project-history" aria-labelledby="client-project-title">
      <h2 id="client-project-title">Сохранённый проект</h2>
      <p className="wizard-step__hint">
        Статус: {project.status === "completed" ? "Завершён" : "В работе"}. Обновлён:{" "}
        {new Date(project.updatedAt).toLocaleString("ru-RU")}.
      </p>

      {ordered.length === 0 ? (
        <p className="wizard-step__hint">У проекта пока нет сохранённых шагов.</p>
      ) : (
        <div className="project-history__sections">
          {ordered.map((step) => (
            <article key={step.id} className="project-history__section">
              <h3>{STEP_TITLES[step.stepType]}</h3>
              <StepBody step={step} labels={labels} />
            </article>
          ))}
        </div>
      )}

      <div className="wizard-step__actions">
        <button type="button" className="wizard-step__primary" onClick={() => onRestore(project)}>
          Продолжить в wizard
        </button>
        <button type="button" onClick={onClose}>
          Закрыть просмотр
        </button>
      </div>
    </section>
  );
}

function StepBody({ step, labels }: { step: ProjectStep; labels: CatalogLabels }) {
  switch (step.stepType) {
    case "industry":
      return (
        <p>
          {labels.industryName ??
            (typeof step.payload.industryId === "string" ? "Отрасль выбрана" : "Отрасль не выбрана")}
        </p>
      );
    case "models":
      return <ModelsBody payload={step.payload} labels={labels} />;
    case "status":
      return (
        <p>
          {typeof step.payload.status === "string"
            ? (STATUS_LABELS[step.payload.status] ?? step.payload.status)
            : "Статус не указан"}
        </p>
      );
    case "params":
      return <ParamsBody payload={step.payload} modelId={step.modelId} labels={labels} />;
    case "result":
      return <ResultBody payload={step.payload} />;
    default:
      return <p>Сохранённый шаг</p>;
  }
}

function ModelsBody({
  payload,
  labels,
}: {
  payload: Record<string, unknown>;
  labels: CatalogLabels;
}) {
  const models = Array.isArray(payload.selectedModels) ? payload.selectedModels : [];
  if (models.length === 0) {
    return <p>Модели не выбраны</p>;
  }

  return (
    <ol className="selection-summary__list">
      {models.map((item, index) => {
        if (typeof item !== "object" || item === null) {
          return null;
        }
        const row = item as { modelId?: unknown; preferredUnitId?: unknown; order?: unknown };
        const modelId = typeof row.modelId === "string" ? row.modelId : null;
        const unitId = typeof row.preferredUnitId === "string" ? row.preferredUnitId : null;
        const card = modelId ? labels.modelsById.get(modelId) : undefined;
        const unit = card?.units.find((entry) => entry.id === unitId);
        const order = typeof row.order === "number" ? row.order : index + 1;
        const title = card?.name ?? (modelId ? "Модель" : "—");
        const unitLabel = unit?.code ?? null;

        return (
          <li key={modelId ?? String(index)}>
            <span className="selection-summary__order" aria-hidden="true">
              {order}
            </span>
            <span className="selection-summary__label">Модель</span>
            <span>
              {title}
              {unitLabel ? ` · ${unitLabel}` : ""}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function ParamsBody({
  payload,
  modelId,
  labels,
}: {
  payload: Record<string, unknown>;
  modelId: string | null;
  labels: CatalogLabels;
}) {
  const values =
    typeof payload.values === "object" && payload.values !== null
      ? Object.entries(payload.values).filter(
          (entry): entry is [string, string] =>
            typeof entry[0] === "string" && typeof entry[1] === "string",
        )
      : [];

  const form = modelId ? labels.formByModelId.get(modelId) : undefined;
  const modelName = form?.modelName ?? (modelId ? labels.modelsById.get(modelId)?.name : null);

  if (values.length === 0) {
    return <p>Параметры не заполнены{modelName ? ` · ${modelName}` : ""}</p>;
  }

  return (
    <>
      {modelName ? <p className="wizard-step__hint">{modelName}</p> : null}
      <table className="result-table">
        <caption className="visually-hidden">Параметры модели</caption>
        <thead>
          <tr>
            <th scope="col">Параметр</th>
            <th scope="col">Значение</th>
          </tr>
        </thead>
        <tbody>
          {values.map(([attributeId, value]) => {
            const field = form?.fields.find((entry) => entry.attributeId === attributeId);
            return (
              <tr key={attributeId}>
                <th scope="row">
                  {field?.name ?? "Параметр"}
                  {field?.unitName ? (
                    <span className="result-table__unit">, {field.unitName}</span>
                  ) : null}
                </th>
                <td>{value}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function ResultBody({ payload }: { payload: Record<string, unknown> }) {
  if (!isCalcPreviewResult(payload.result)) {
    return <p>Результат не сохранён</p>;
  }

  const result = {
    ...payload.result,
    restrictedOutputs: payload.result.restrictedOutputs ?? [],
  };
  return (
    <>
      <p>
        {result.modelName}
        {result.unitCode ? ` · ${result.unitCode}` : ""}
      </p>
      {result.outputs.length === 0 && result.restrictedOutputs.length === 0 ? (
        <p className="wizard-step__hint">Нет величин для отображения</p>
      ) : null}
      {result.outputs.length > 0 ? (
        <table className="result-table">
          <caption className="visually-hidden">Результаты расчёта</caption>
          <thead>
            <tr>
              <th scope="col">Параметр</th>
              <th scope="col">Значение</th>
            </tr>
          </thead>
          <tbody>
            {result.outputs.map((row) => (
              <tr key={row.attributeId}>
                <th scope="row">
                  {row.name}
                  {row.unitName ? (
                    <span className="result-table__unit">, {row.unitName}</span>
                  ) : null}
                </th>
                <td>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {result.restrictedOutputs.length > 0 ? (
        <div className="result-restricted">
          <p className="wizard-step__hint">Полный тяговый расчёт</p>
          <table className="result-table">
            <caption className="visually-hidden">Параметры с ограниченным доступом</caption>
            <thead>
              <tr>
                <th scope="col">Параметр</th>
                <th scope="col">Доступ</th>
              </tr>
            </thead>
            <tbody>
              {result.restrictedOutputs.map((row) => (
                <tr key={row.attributeId}>
                  <th scope="row">
                    {row.name}
                    {row.unitName ? (
                      <span className="result-table__unit">, {row.unitName}</span>
                    ) : null}
                  </th>
                  <td>
                    <span className="result-restricted__hint">доступ по запросу</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

function findIndustryId(steps: ProjectStep[]): string | null {
  const industry = steps.find((step) => step.stepType === "industry");
  return typeof industry?.payload.industryId === "string" ? industry.payload.industryId : null;
}

function collectModelIds(steps: ProjectStep[]): string[] {
  const ids = new Set<string>();
  for (const step of steps) {
    if (typeof step.modelId === "string") {
      ids.add(step.modelId);
    }
    if (step.stepType === "models" && Array.isArray(step.payload.selectedModels)) {
      for (const item of step.payload.selectedModels) {
        if (typeof item === "object" && item !== null && typeof (item as { modelId?: unknown }).modelId === "string") {
          ids.add((item as { modelId: string }).modelId);
        }
      }
    }
  }
  return [...ids].sort();
}

function isCalcPreviewResult(value: unknown): value is CalcPreviewResult {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as { modelId?: unknown; modelName?: unknown; outputs?: unknown };
  return typeof row.modelId === "string" && typeof row.modelName === "string" && Array.isArray(row.outputs);
}
